import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store';
import { selectRoles } from '@/store/slices/authSlice';
import { ShieldX, ArrowLeft } from 'lucide-react';

interface AccessDeniedProps {
    requiredPermission: string | string[];
}

/**
 * AccessDenied: shown when a user navigates to a route they don't have permission for.
 *
 * Displays:
 * - "You don't have access" message
 * - User's current role
 * - The required permission
 * - Link back to dashboard
 */
const AccessDenied: React.FC<AccessDeniedProps> = ({ requiredPermission }) => {
    const navigate = useNavigate();
    const roles = useAppSelector(selectRoles);

    const permissionDisplay = Array.isArray(requiredPermission)
        ? requiredPermission.join(' or ')
        : requiredPermission;

    const roleDisplay = roles.length > 0 ? roles.join(', ') : 'No role assigned';

    return (
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="w-full max-w-md text-center space-y-6">
                {/* Icon */}
                <div className="flex justify-center">
                    <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                        <ShieldX className="w-8 h-8 text-red-500" />
                    </div>
                </div>

                {/* Heading */}
                <div className="space-y-2">
                    <h2 className="text-xl font-semibold text-gray-900">
                        You don't have access to this
                    </h2>
                    <p className="text-sm text-gray-500">
                        Contact your organization admin to request access.
                    </p>
                </div>

                {/* Details */}
                <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2">
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Your role:</span>
                        <span className="font-medium text-gray-700">{roleDisplay}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Required:</span>
                        <span className="font-medium text-gray-700 font-mono text-xs">
                            {permissionDisplay}
                        </span>
                    </div>
                </div>

                {/* Back button */}
                <button
                    onClick={() => navigate('/settings/profile')}
                    className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Profile
                </button>
            </div>
        </div>
    );
};

export default AccessDenied;
